<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import { useForm } from 'vee-validate';

import api from '@/api';
import { defaultAdditionalFundRequired, defaultIncomingProperty, defaultLoanDetails } from '@/helpers/builder';
import {
  AdditionalFundsPurpose,
  CommercialAdditionalFundsPurposeOptions,
  depositTypeOptions,
  LoanType,
  LoanPurposeAdditionalFundsPurposeOptions,
  propertyPurposeOptions,
  stampDutyStatusOptions,
} from '@/helpers/const';
import { useBrokerLoanAppProcess } from '@/helpers/useBrokerLoanAppProcess';
import { buildAdditionalFundsRequiredProperties } from '@/helpers/additionalFundsRequiredProperties';
import { snakeCase } from '@/helpers/format';
import { useApplicationApi } from '@/helpers/useApplicationApi';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';
import { DraftEventName } from '@/helpers/draftEvents';
import { loanPurposeSchema } from '@/validation';

import type { AdditionalFundsRequiredDto, ListAdditionalFundsPurposes } from '@/api/backend/public';
import type { AdditionalFundsRequiredEntry, IncomingProperty, LoanApplication, LoanDetails, Address } from '@/types';
import type { AdditionalFundsRequiredProperty } from '@/types/AdditionalFundsRequiredProperty';

let listAdditionalFundsPurposes: ListAdditionalFundsPurposes = [];
const isLoading = ref(false);
const { state, loanApp, goBack, saveAndGoNext } = useBrokerLoanAppProcess();
const { saveApplicationToDraft } = useApplicationApi();
const { isPurchaseOnlyLoanTypeEnabled } = useFeatureFlags();

const PURCHASE_ONLY_OPTION_TOOLTIP = 'Purchase only applications are for residential / commercial purposes only';

const {
  values,
  meta,
  submitCount,
  handleSubmit: validateSubmit,
} = useForm<AdditionalFundsRequiredEntry>({
  validationSchema: loanPurposeSchema,
  initialValues: {
    ...loanApp.value.additionalFundsRequiredProperties,
    ...loanApp.value.additionalFundsRequired,
    isAdditionalFundsRequired: undefined,
  },
  validateOnMount: false,
});

const fundsPurposeOptions = computed(() => {
  const { [AdditionalFundsPurpose.OTHER]: other, ...baseOptions } = LoanPurposeAdditionalFundsPurposeOptions;
  const merged = { ...baseOptions, ...CommercialAdditionalFundsPurposeOptions };
  if (isPurchaseOnlyLoanTypeEnabled()) {
    return {
      ...merged,
      [AdditionalFundsPurpose.PURCHASE_ONLY]: 'Purchase Only',
      [AdditionalFundsPurpose.OTHER]: other,
    };
  }
  return { ...merged, [AdditionalFundsPurpose.OTHER]: other };
});

const fundsPurposeOptionTooltips = computed<Record<string, string>>(() => {
  if (!isPurchaseOnlyLoanTypeEnabled()) return {} as Record<string, string>;

  return {
    [AdditionalFundsPurpose.PURCHASE_ONLY]: PURCHASE_ONLY_OPTION_TOOLTIP,
  };
});

const descriptionPrompts: Record<string, string> = {
  [AdditionalFundsPurpose.COSMETIC_IMPROVEMENTS_BEFORE_SELLING]: 'Explain the cosmetic improvement being done',
  [AdditionalFundsPurpose.OTHER]: 'Provide the details of the additional borrowing purposes',
  [AdditionalFundsPurpose.BUSINESS_PURPOSE]: 'Explain the business purpose requirements',
  [AdditionalFundsPurpose.COMPLETE_PROJECT_FINANCE]: 'Explain the project finance requirements',
};

const handleSubmit = validateSubmit(async (values) => {
  isLoading.value = true;
  const isPurchaseOnly = values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_ONLY;
  const additionalFundsPurposeId = listAdditionalFundsPurposes?.find(
    (item) => snakeCase(item.title) === values.reasonForFundsOption,
  )?.id;
  const isVacantOrRetirement =
    values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_VACANT_LAND ||
    values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME;

  // Only populate fundsRequiredAmount for non-vacant/retirement options
  const additionalFundsRequired: AdditionalFundsRequiredDto = Object.assign(defaultAdditionalFundRequired(), {
    fundsRequiredDate: isVacantOrRetirement ? values.settlementDate : values.fundsRequiredDate,
    fundsRequiredAmount: isVacantOrRetirement ? 0 : values.fundsRequiredAmount,
    additionalFundsPurposeId: additionalFundsPurposeId ?? '',
    description: values.description,
  });

  const isContributingFundsTowardsPurchase =
    ((values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME &&
      values.isThereContractForSale !== undefined) ||
      values.reasonForFundsOption === AdditionalFundsPurpose.OTHER ||
      values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_VACANT_LAND) &&
    values.contributingFundsTowardsPurchase;
  const loanDetails: LoanDetails = Object.assign(defaultLoanDetails(), {
    savingsAmount: isPurchaseOnly ? 0 : isContributingFundsTowardsPurchase ? values.fundsToContributeAmount : 0,
  });

  const emptyAddress = {
    addressLine: '',
    addressLine2: '',
    locality: '',
    state: '',
    postcode: '2000',
    countryCode: 'au',
  } as Address;

  const property: IncomingProperty = Object.assign(defaultIncomingProperty(), {
    settlementDate: additionalFundsRequired.fundsRequiredDate,
    address: values.propertyAddress ?? emptyAddress,
    contractForSale: values.isThereContractForSale ?? false,
    purchaseAmount: values.purchasePrice ?? 0,
    depositAmount: values.depositAmount ?? 0,
    depositType: values.depositType ?? 'cash_payment',
    stampDutyStatus: values.stampDutyStatus ?? 'already_paid',
    stampDuty: 0,
  });

  const contributionFieldsClearsForPurchaseOnly: Pick<
    AdditionalFundsRequiredProperty,
    'contributingFundsTowardsPurchase' | 'fundsToContributeAmount'
  > = {
    contributingFundsTowardsPurchase: false,
    fundsToContributeAmount: 0,
  };

  // Only a purchase purpose keeps the purchase property; hidden purchase answers are dropped (JS-10505).
  const additionalFundsRequiredProperties: AdditionalFundsRequiredProperty = {
    ...buildAdditionalFundsRequiredProperties(values),
    ...(isPurchaseOnly ? contributionFieldsClearsForPurchaseOnly : {}),
  };

  const wasPurchaseOnly = loanApp.value.loanType === LoanType.PURCHASE_ONLY;
  const nextLoanType = isPurchaseOnly
    ? LoanType.PURCHASE_ONLY
    : wasPurchaseOnly
    ? LoanType.SINGLE_SECURITY
    : loanApp.value.loanType;
  const purchaseOnlyModeChanged = wasPurchaseOnly !== isPurchaseOnly;

  const incomingProperties =
    isPurchaseOnly || nextLoanType === LoanType.SINGLE_SECURITY
      ? isPurchaseOnly && !purchaseOnlyModeChanged
        ? [...loanApp.value.incomingProperties]
        : []
      : [property];
  const loanTypeUpdate = nextLoanType !== loanApp.value.loanType ? { loanType: nextLoanType } : {};
  const propertyResetOnPurchaseOnlyToggle: Partial<LoanApplication> = purchaseOnlyModeChanged
    ? ({
        outgoingProperties: [],
        supportingProperties: [],
        endDebt: 0,
        ...(isPurchaseOnly && {
          additionalFundsRequiredProperties: {
            ...loanApp.value.additionalFundsRequiredProperties,
            ...contributionFieldsClearsForPurchaseOnly,
          },
        }),
      } as Partial<LoanApplication>)
    : {};
  const exitStrategyResetLeavingPurchaseOnly =
    wasPurchaseOnly && !isPurchaseOnly ? { exitStrategy: undefined, exitStrategyDetails: undefined } : {};

  let applicationDraftId = loanApp.value.applicationDraftId;
  try {
    if (loanApp.value.brokerId) {
      applicationDraftId = await saveApplicationToDraft(
        state.value.context,
        {
          ...loanApp.value,
          ...loanTypeUpdate,
          ...propertyResetOnPurchaseOnlyToggle,
          ...exitStrategyResetLeavingPurchaseOnly,
          loanDetails,
          additionalFundsRequired,
          additionalFundsRequiredProperties: additionalFundsRequiredProperties,
          incomingProperties,
        },
        DraftEventName.LoanPurpose,
      );
    }
    saveAndGoNext({
      ...loanTypeUpdate,
      ...propertyResetOnPurchaseOnlyToggle,
      ...exitStrategyResetLeavingPurchaseOnly,
      loanDetails,
      additionalFundsRequired,
      additionalFundsRequiredProperties: additionalFundsRequiredProperties,
      incomingProperties,
      applicationDraftId,
    });
  } catch (error) {
    console.error('Failed to save draft:', error);
  } finally {
    isLoading.value = false;
  }
});

const isReady = ref(false);
onBeforeMount(async () => {
  isReady.value = false;
  try {
    listAdditionalFundsPurposes = await api.backend.public.listAdditionalFundsPurposes();
  } finally {
    isReady.value = true;
  }
});
</script>

<template>
  <div>
    <div class="mb-8 pb-2">
      <h2 class="text-2xl font-medium">Tell us about the <BxHighlight>loan purpose</BxHighlight>?</h2>
    </div>
    <form style="width: 500px" @submit="handleSubmit">
      <div>
        <BxField v-slot="{ field }" name="reasonForFundsOption">
          <p v-if="!values.reasonForFundsOption" class="mb-3">Select all that apply.</p>
          <BxSelect
            v-bind="field"
            :label="values.reasonForFundsOption ? 'Loan purpose' : 'Purpose of the funds'"
            label-type="floating"
            :list-items="fundsPurposeOptions"
            :option-tooltips="fundsPurposeOptionTooltips"
            class="form-control"
          />
        </BxField>
        <BxField
          v-if="
            values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME ||
            values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_VACANT_LAND
          "
          v-slot="{ field }"
          name="propertyAddress"
        >
          <BxAddressInput v-bind="field" label="Property Address" label-type="floating" class="form-control" />
        </BxField>
        <BxField
          v-if="values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_VACANT_LAND"
          v-slot="{ field }"
          name="propertyPurpose"
        >
          <BxRadio
            v-bind="field"
            :options="propertyPurposeOptions"
            label="What is the purpose of this property?"
            class="form-control"
            block
          />
        </BxField>
        <BxField
          v-if="
            values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME ||
            values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_VACANT_LAND
          "
          v-slot="{ field }"
          name="isThereContractForSale"
        >
          <div class="mb-4 mt-4">
            <BxRadio
              v-bind="field"
              label="Is there a contract for sale?"
              label-type="default"
              class="form-control"
              boolean
            />
          </div>
        </BxField>
        <template
          v-if="
            (values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_VACANT_LAND ||
              values.reasonForFundsOption ===
                AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME) &&
            values.isThereContractForSale !== undefined
          "
        >
          <BxField v-slot="{ field }" name="settlementDate">
            <BxDatePicker
              v-bind="field"
              :label="values.isThereContractForSale ? 'Settlement date' : 'Estimated settlement date'"
              label-type="floating"
              placeholder="DD/MM/YYYY"
              class="form-control"
              :min-date="new Date()"
            />
          </BxField>
          <BxField v-slot="{ field }" name="purchasePrice">
            <BxNumberInput
              v-bind="field"
              :label="values.isThereContractForSale ? 'Purchase price' : 'Estimated purchase price'"
              label-type="floating"
              leading-symbol="$"
              class="form-control"
            />
          </BxField>
          <BxField v-slot="{ field }" name="depositAmount">
            <BxNumberInput
              v-bind="field"
              :label="values.isThereContractForSale ? 'Deposit amount' : 'Estimated deposit amount'"
              label-type="floating"
              leading-symbol="$"
              class="form-control"
            />
          </BxField>
          <BxField v-slot="{ field }" name="depositType">
            <BxRadio
              v-bind="field"
              :options="depositTypeOptions"
              :label="values.isThereContractForSale ? 'How was your deposit paid?' : 'How is the deposit being paid?'"
              label-type="default"
              class="form-control"
            />
          </BxField>
          <BxField
            v-if="
              values.reasonForFundsOption !== AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME
            "
            v-slot="{ field }"
            name="stampDutyStatus"
          >
            <BxRadio
              v-bind="field"
              :options="stampDutyStatusOptions"
              label="How will stamp duty be paid?"
              label-type="default"
              class="form-control incoming-margin-top"
              block
            />
          </BxField>
          <BxField v-slot="{ field }" name="transferFeeCovered">
            <BxRadio
              v-bind="field"
              label="Would you like the transfer fee to be covered?"
              label-type="default"
              class="form-control incoming-margin-top"
              boolean
            />
          </BxField>
        </template>
        <template
          v-if="
            values.reasonForFundsOption !== AdditionalFundsPurpose.PURCHASE_VACANT_LAND &&
            values.reasonForFundsOption !== AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME &&
            values.reasonForFundsOption !== AdditionalFundsPurpose.PURCHASE_ONLY
          "
        >
          <BxField v-if="values.reasonForFundsOption" v-slot="{ field }" name="fundsRequiredAmount">
            <BxNumberInput
              v-bind="field"
              label="Funds required"
              :max="999999999"
              label-type="floating"
              leading-symbol="$"
              class="form-control"
            />
          </BxField>
          <BxField v-if="values.reasonForFundsOption" v-slot="{ field }" name="fundsRequiredDate">
            <BxDatePicker
              v-bind="field"
              label="Funds required by"
              label-type="floating"
              placeholder="DD/MM/YYYY"
              class="form-control"
              :min-date="new Date()"
            />
          </BxField>
        </template>
        <BxField v-if="descriptionPrompts[values.reasonForFundsOption]" v-slot="{ field }" name="description">
          <div class="mt-6">
            <p>{{ descriptionPrompts[values.reasonForFundsOption] }}</p>
            <BxTextarea class="mt-2" :rows="5" v-bind="field" />
          </div>
        </BxField>
        <BxField
          v-if="
            ((values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME ||
              values.reasonForFundsOption === AdditionalFundsPurpose.PURCHASE_VACANT_LAND) &&
              values.isThereContractForSale !== undefined) ||
            values.reasonForFundsOption === AdditionalFundsPurpose.OTHER
          "
          v-slot="{ field }"
          name="contributingFundsTowardsPurchase"
        >
          <div class="mt-4">
            <BxRadio
              v-bind="field"
              label="Is your client contributing funds towards the purchase?"
              label-type="default"
              class="form-control"
              boolean
            />
          </div>
        </BxField>
        <BxField v-if="values.contributingFundsTowardsPurchase" v-slot="{ field }" name="fundsToContributeAmount">
          <div class="mt-4">
            <BxNumberInput
              v-bind="field"
              label="Funds to contribute amount"
              label-type="floating"
              leading-symbol="$"
              class="form-control"
            />
          </div>
        </BxField>
      </div>
      <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
        Please review the error messages above.
      </BxErrorPanel>
      <div class="mt-6">
        <BxButton variant="primary" :loading="!isReady || isLoading" type="submit">Save and continue</BxButton>
        <BxButton v-if="state.nextEvents.includes('BACK')" variant="link" class="ml-4" @click="goBack()">Back</BxButton>
      </div>
    </form>
  </div>
</template>
